import React from 'react';

/**
 * A permanently-mounted, visually-hidden live region for announcing transient
 * status to screen readers.
 *
 * ⚠️ THE WHOLE POINT IS THAT IT IS ALWAYS MOUNTED, and that is not a style choice
 * (item 10, operator-ruled 2026-08-14). Assistive technology establishes its
 * "watch this element for changes" behaviour when it first encounters the region.
 * A live region that is created at the same moment as its own text is frequently
 * NOT announced, because there was no prior state for the change to be measured
 * against. So this renders on every pass, empty when there is nothing to say, and
 * callers change its `message` — they must NOT conditionally render it.
 *
 * ⚠️ DO NOT "optimise" this by returning `null` when `message` is empty. That
 * restores the exact defect it was written to fix, and the failure is invisible to
 * every automated check in this repo — the markup would still look correct.
 *
 * WHY POLITE, NOT ASSERTIVE: `aria-live="polite"` waits for a natural pause;
 * `role="alert"` (assertive) interrupts whatever the user is currently hearing.
 * These messages are soft degradations — "search is temporarily unavailable", "some
 * suburbs may be missing" — where the visitor can simply try again. Interrupting
 * them mid-sentence is disproportionate. Operator-ruled with that trade stated.
 *
 * `aria-atomic` makes the region announce as one phrase rather than reading only
 * the words that changed, which matters because these messages are replaced
 * wholesale rather than edited.
 *
 * COVERS: the two suburb-search surfaces that carry outage messaging — the filter
 * panel and the homepage hero — i.e. 2 of the app's search surfaces, and all THREE
 * messages that share those slots (full outage on either surface, plus the
 * one-state partial outage on the homepage).
 * DOES NOT COVER: any other transient status in the app. Several places already
 * roll their own `role="status"` (e.g. FavouritesPage's loading text); this does
 * not replace them and no sweep was done. SIBLING: nothing else centralises this.
 */
export interface LiveAnnouncerProps {
    /** The text to announce. Empty string = nothing to say; the region still renders. */
    message: string;
}

const LiveAnnouncer: React.FC<LiveAnnouncerProps> = ({ message }) => (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {message}
    </div>
);

export default LiveAnnouncer;
